import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import styles from './knowledge-items-widget.module.scss';
import ReactPaginate from 'react-paginate';
import { NoContent } from 'src/components/common';
import { KnowledgeDeletePopup } from 'src/components/knowledge-content/knowledge-content-editor';
import { KnowledgeMovePopup } from 'src/pages/application/knowledge/components/knowledge-row/knowledgeMove';
import { KnowledgeWrapperComponent } from 'src/pages/application/knowledge/components/knowledge-row';
import { Loader } from 'src/components/core';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'shared/constants';
import { ListItemsNavigator } from 'src/components/common/items-navigator';

const KnowledgeItemsWidgetComponent = React.memo(function KnowledgeItemsWidgetComponent({
  title = '',
  items = [],
  itemsPerPage = 5,
  paginated = false,
  adminMode = false,
  id,
  knowledgeItem = null,
  allowEditExpired = false,
}) {
  const [itemOffset, setItemOffset] = useState(0);
  const navigate = useNavigate();
  const [deleteKnowledgeItem, setDeleteKnowledgeItem] = React.useState(null);
  const [knowledgeEntityMove, setKnowledgeEntityMove] = React.useState(null);

  // Delete knowledge items
  const deleteKnowledgeAction = React.useCallback(({ id, title }) => {
    // TODO: analytics
    setDeleteKnowledgeItem({ id, title });
  }, []);

  const onFinishDelete = React.useCallback(() => {
    setDeleteKnowledgeItem(null);
  }, []);

  const editOrCreateKnowledge = React.useCallback(
    ({ item = {} }) => {
      const targetPath = item.id
        ? ROUTES.KNOWLEDGE_ITEMS_EDIT.replace(':id', item.id)
        : ROUTES.KNOWLEDGE_ITEMS_NEW;
      navigate(targetPath);
    },
    [navigate],
  );

  // Move knowledge items
  const moveKnowledgeAction = React.useCallback(({ id, title, ownerIds }) => {
    // TODO: analytics
    setKnowledgeEntityMove({ id, title, ownerIds });
  }, []);

  const onFinishMove = React.useCallback(() => {
    setKnowledgeEntityMove(null);
  }, []);

  const currentItems = useMemo(() => {
    if (!items) return null;
    if (!paginated) return items;

    const endOffset = itemOffset + itemsPerPage;
    return items.slice(itemOffset, endOffset);
  }, [items, paginated, itemOffset, itemsPerPage]);

  const pageCount = useMemo(() => {
    if (!items || !paginated) return 0;
    return Math.ceil(items.length / itemsPerPage);
  }, [items, paginated, itemsPerPage]);

  // Invoke when user click to request another page.
  const handlePageClick = event => {
    const newOffset = (event.selected * itemsPerPage) % items.length;
    setItemOffset(newOffset);
  };

  const renderContent = () => {
    if (currentItems === null) {
      return <Loader />;
    }

    if (currentItems.length === 0) {
      return <NoContent className={styles.noContent} />;
    }

    return (
      <ListItemsNavigator orientation="vertical" aria-label={title}>
        {currentItems.map(item => (
          <KnowledgeWrapperComponent
            key={item.id}
            item={item}
            draggable={false}
            adminMode={adminMode}
            onDelete={deleteKnowledgeAction}
            onUpdate={editOrCreateKnowledge}
            onMove={moveKnowledgeAction}
            showSpecialty
            allowEditExpired={allowEditExpired}
          />
        ))}
      </ListItemsNavigator>
    );
  };

  return (
    <div className={styles.content}>
      <label className={styles.title}>{title}</label>
      <div className={classNames(styles.widgetdItems, { [styles.loading]: currentItems === null })}>
        {renderContent()}
        {paginated && items?.length > itemsPerPage && (
          <ReactPaginate
            breakLabel="..."
            nextLabel=">"
            onPageChange={handlePageClick}
            pageRangeDisplayed={3}
            pageCount={pageCount}
            previousLabel="<"
            className={styles.pagination}
            activeClassName={styles.active}
            nextClassName={styles.nextArrow}
            previousClassName={styles.previousArrow}
            disabledClassName={styles.disabledArrow}
            renderOnZeroPageCount={null}
          />
        )}
      </div>
      <KnowledgeDeletePopup
        open={!!deleteKnowledgeItem}
        entity={deleteKnowledgeItem}
        onFinish={onFinishDelete}
      />

      <KnowledgeMovePopup
        isOpen={!!knowledgeEntityMove}
        categoryId={id}
        item={knowledgeEntityMove}
        onFinish={onFinishMove}
        enableCreateLink
      />
    </div>
  );
});

export { KnowledgeItemsWidgetComponent };
